/**
 * Parse Admin "Iframe Custom Attributes" for catalog service items.
 *
 * Preferred (HTML-like, same idea as <iframe …>):
 *   height="175" scrolling="no" styles="marginBottom: 153px; borderRadius: 12px"
 *
 * Legacy (still supported):
 *   height=175|scrolling=no|styles=margin-bottom~12px,border-radius~12px
 *
 * Optional compact (small player) block in the same DB string — no schema change:
 *   height="352" scrolling="no"
 *   @@@small@@@
 *   height="175" scrolling="no"
 *
 * Full-size is the default. Compact is only used when that section exists and
 * the player is in a compact layout (catalog browse grid for songs only —
 * albums/playlists always use full-size).
 */

/** Delimiter stored inside iframeCustomAttributes between full and small blocks */
export const IFRAME_ATTRS_SMALL_MARKER = "@@@small@@@";

const toAttrKey = (rawName) => {
  if (rawName == null) return "";
  return String(rawName)
    .trim()
    .replace(/^["']|["']$/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-([a-zA-Z0-9])/g, (_, c) => c.toUpperCase())
    .replace(/^[A-Z]/, (c) => c.toLowerCase());
};

/** CSS / React style key → camelCase for React inline styles */
const toStyleKey = (rawName) => {
  if (rawName == null) return "";
  const s = String(rawName).trim().replace(/^["']|["']$/g, "");
  // Already camelCase
  if (/^[a-z]+[A-Z]/.test(s) || /^[a-z]+$/.test(s)) {
    return s.replace(/-([a-z])/gi, (_, c) => c.toUpperCase());
  }
  return s.replace(/-([a-z])/gi, (_, c) => c.toUpperCase());
};

/**
 * Parse a styles= value into a React style object.
 * Accepts:
 *   marginBottom: 153px; border-radius: 12px
 *   marginBottom: 153px, borderRadius: 12px
 *   margin-bottom~12px,border-radius~12px   (legacy)
 */
export const parseIframeStylesValue = (raw) => {
  if (raw == null) return {};
  let text = String(raw).trim();
  if (!text) return {};
  // Strip wrapping quotes if the whole value was quoted twice
  if (
    (text.startsWith('"') && text.endsWith('"')) ||
    (text.startsWith("'") && text.endsWith("'"))
  ) {
    text = text.slice(1, -1).trim();
  }

  const out = {};

  // Legacy tilde form: prop~value,prop~value
  if (text.includes("~") && !text.includes(":")) {
    text.split(",").forEach((chunk) => {
      const [k, ...rest] = chunk.split("~");
      const key = toStyleKey(k);
      const val = rest.join("~").trim();
      if (key && val !== "") out[key] = val;
    });
    return out;
  }

  // CSS-like: prop: value; prop: value  (commas also allowed as separators)
  // Split on ; or , that appear between declarations (not inside values — values are simple)
  const parts = text.split(/[;]/).flatMap((p) => {
    // Only split on comma if it looks like another "prop: value" pair
    if (/,\s*[a-zA-Z-][\w-]*\s*:/.test(p)) {
      return p.split(/,(?=\s*[a-zA-Z-][\w-]*\s*:)/);
    }
    return [p];
  });

  parts.forEach((part) => {
    const trimmed = part.trim();
    if (!trimmed) return;
    const colon = trimmed.indexOf(":");
    if (colon === -1) return;
    const key = toStyleKey(trimmed.slice(0, colon));
    const val = trimmed.slice(colon + 1).trim();
    if (key && val !== "") out[key] = val;
  });

  return out;
};

/**
 * Split raw string into [name, value] pairs.
 * HTML-like: height="175" styles="a: b"
 * Pipe: height=175|styles=a~b
 */
const extractPairs = (raw) => {
  const text = String(raw).trim();
  if (!text) return [];

  // Pipe-separated legacy (still the main format in existing data)
  if (text.includes("|")) {
    return text
      .split("|")
      .map((chunk) => chunk.trim())
      .filter(Boolean)
      .map((chunk) => {
        // name=value or name:value (first separator only)
        const m = chunk.match(/^([^=:\s]+)\s*[=:]\s*([\s\S]*)$/);
        if (m) return [m[1].trim(), m[2].trim()];
        // flag
        return [chunk.replace(/^["']|["']$/g, "").trim(), "true"];
      })
      .filter(([name]) => name);
  }

  // HTML-like: attr="value" or attr='value' or attr=value
  const pairs = [];
  const re =
    /([a-zA-Z_:][\w:.-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"']+))/g;
  let match;
  while ((match = re.exec(text)) !== null) {
    const name = match[1];
    const value = match[2] ?? match[3] ?? match[4] ?? "";
    pairs.push([name, value]);
  }

  // Bare flags without values (rare)
  const withoutPairs = text
    .replace(re, " ")
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean);
  withoutPairs.forEach((flag) => {
    if (/^[a-zA-Z_][\w.-]*$/.test(flag)) pairs.push([flag, "true"]);
  });

  return pairs;
};

/** Attrs that identify / load content — not player chrome defaults. */
const IFRAME_IDENTITY_ATTRS = new Set(["src", "srcdoc"]);

/**
 * Decode common HTML entities in pasted markup attribute values.
 * @param {string} s
 * @returns {string}
 */
const decodeHtmlEntities = (s) =>
  String(s)
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");

/**
 * Serialize one attribute for the Admin full/small player fields.
 * Values are double-quoted; internal `"` become `&quot;`.
 * @param {string} name
 * @param {string} value
 * @returns {string}
 */
const formatIframeAttrPair = (name, value) => {
  const n = String(name).trim();
  if (!n) return "";
  const v = value == null ? "" : String(value);
  if (v === "" || v === "true") {
    // Boolean-ish / empty flags still get a value so extractPairs stays happy
    return `${n}="true"`;
  }
  const escaped = v.replace(/"/g, "&quot;");
  return `${n}="${escaped}"`;
};

/**
 * Extract iframe chrome attributes from a pasted service embed snippet.
 * Omits `src` / `srcdoc` (those belong on the item URL). Maps HTML `style`
 * → Admin `styles` (same key the player reads).
 *
 * @param {string|null|undefined} raw - full `<iframe …>` HTML or bare attr list
 * @returns {string} e.g. height="352" scrolling="no" frameborder="0" allow="…"
 */
export const extractIframeAttributesFromEmbed = (raw) => {
  if (raw == null) return "";
  const text = String(raw).trim();
  if (!text) return "";

  // Prefer the first <iframe …> opening tag’s attribute list
  let attrSource = text;
  const tagMatch = text.match(/<iframe\b([^>]*)\/?>/i);
  if (tagMatch) {
    attrSource = tagMatch[1] || "";
  } else if (/</.test(text)) {
    // Other markup without a clean iframe open — fail rather than mis-parse
    return "";
  } else if (!/=\s*/.test(text)) {
    // Bare words (no name=value) would be misread as boolean flags
    return "";
  }

  const pairs = extractPairs(attrSource);
  if (pairs.length === 0) return "";

  const formatted = [];
  pairs.forEach(([rawName, rawValue]) => {
    // Lowercase so React-ish frameBorder / allowFullScreen match player keys
    let name = String(rawName || "")
      .trim()
      .toLowerCase();
    if (!name) return;
    if (IFRAME_IDENTITY_ATTRS.has(name)) return;

    // HTML style= → Admin styles= (parseIframeCustomAttributes expects "styles")
    if (name === "style") name = "styles";

    let value =
      rawValue == null ? "" : decodeHtmlEntities(String(rawValue).trim());
    // Strip one layer of wrapping quotes after entity decode
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }

    const piece = formatIframeAttrPair(name, value);
    if (piece) formatted.push(piece);
  });

  return formatted.join(" ");
};

/**
 * Split the stored Admin string into full (default) and optional small sections.
 * @returns {{ full: string, small: string }}
 */
export const splitIframeAttributeSections = (raw) => {
  if (raw == null || typeof raw !== "string") {
    return { full: "", small: "" };
  }
  const text = raw.trim();
  if (!text) return { full: "", small: "" };

  const idx = text
    .toLowerCase()
    .indexOf(IFRAME_ATTRS_SMALL_MARKER.toLowerCase());
  if (idx === -1) {
    return { full: text, small: "" };
  }

  const full = text.slice(0, idx).trim();
  const small = text.slice(idx + IFRAME_ATTRS_SMALL_MARKER.length).trim();
  return { full, small };
};

/**
 * Join Admin dual inputs back into one DB field value.
 * Omits the small section when empty (backward compatible single block).
 */
export const joinIframeAttributeSections = ({ full = "", small = "" } = {}) => {
  const f = full == null ? "" : String(full).trim();
  const s = small == null ? "" : String(small).trim();
  if (!s) return f;
  if (!f) return `${IFRAME_ATTRS_SMALL_MARKER}\n${s}`;
  return `${f}\n${IFRAME_ATTRS_SMALL_MARKER}\n${s}`;
};

/**
 * @param {string|null|undefined} raw - one section (full or small), not the combined DB string
 * @returns {Record<string, string|Record<string, string>>}
 */
export const parseIframeCustomAttributes = (raw) => {
  if (raw == null) return {};
  if (typeof raw !== "string") return {};
  const trimmed = raw.trim();
  if (!trimmed) return {};

  const output = {};
  const pairs = extractPairs(trimmed);

  pairs.forEach(([rawName, rawValue]) => {
    const name = toAttrKey(rawName);
    if (!name) return;

    let value = rawValue == null ? "" : String(rawValue).trim();
    // Strip one layer of wrapping quotes
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }

    if (name === "styles") {
      output.styles = parseIframeStylesValue(value);
      return;
    }

    if (value === "") {
      output[name] = "true";
      return;
    }

    output[name] = value;
  });

  return output;
};

/**
 * Parse the combined DB field and pick full or compact attrs.
 * @param {string|null|undefined} raw
 * @param {"full"|"compact"} layout - compact uses small section when present
 *   (callers should only pass "compact" for songs in the catalog browse grid)
 */
export const parseIframeCustomAttributesForLayout = (
  raw,
  layout = "full",
) => {
  const { full, small } = splitIframeAttributeSections(raw);
  const useCompact =
    layout === "compact" && small && String(small).trim() !== "";
  return parseIframeCustomAttributes(useCompact ? small : full);
};

export default parseIframeCustomAttributes;
